import { ColorPicker, type RGB } from "./ColorPicker";
import type { TypeLayer, AntiAliasMode } from "../../core/document/TypeLayer";

// ---------- Public types ----------

export interface TypeProperties {
  fontFamily: string;
  fontStyle: string;
  fontSize: number;
  leading: number;
  tracking: number;
  baselineShift: number;
  color: { r: number; g: number; b: number };
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  align: "left" | "center" | "right" | "justify";
  fauxBold: boolean;
  fauxItalic: boolean;
  allCaps: boolean;
  smallCaps: boolean;
  superscript: boolean;
  subscript: boolean;
  antiAlias: AntiAliasMode;
}

// ---------- Constants ----------

const FONT_FAMILIES = [
  "Arial",
  "Helvetica",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Verdana",
  "Trebuchet MS",
  "Impact",
  "Comic Sans MS",
];

let _systemFonts: string[] | null = null;

export async function loadSystemFonts(): Promise<string[]> {
  if (_systemFonts) return _systemFonts;
  try {
    if ("queryLocalFonts" in window) {
      const fonts = await (window as any).queryLocalFonts();
      const seen = new Set<string>();
      const result: string[] = [];
      for (const f of fonts) {
        if (!seen.has(f.family)) {
          seen.add(f.family);
          result.push(f.family);
        }
      }
      _systemFonts = result.sort((a, b) => a.localeCompare(b));
      return _systemFonts;
    }
  } catch {
    // Fall back to hardcoded list
  }
  _systemFonts = [...FONT_FAMILIES];
  return _systemFonts;
}

function getAvailableFonts(): string[] {
  return _systemFonts || FONT_FAMILIES;
}

const FONT_STYLES = ["Regular", "Italic", "Bold", "Bold Italic"];

const ANTI_ALIAS_MODES: AntiAliasMode[] = ["none", "sharp", "crisp", "strong", "smooth"];

const DEFAULTS: TypeProperties = {
  fontFamily: "Arial",
  fontStyle: "Regular",
  fontSize: 12,
  leading: 14.4,
  tracking: 0,
  baselineShift: 0,
  color: { r: 0, g: 0, b: 0 },
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  align: "left",
  fauxBold: false,
  fauxItalic: false,
  allCaps: false,
  smallCaps: false,
  superscript: false,
  subscript: false,
  antiAlias: "smooth",
};

// ---------- DOM helpers ----------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

// ---------- Component ----------

export class CharacterPanel {
  private built = false;
  private root!: HTMLDivElement;

  // Controls
  private familySelect!: HTMLSelectElement;
  private styleSelect!: HTMLSelectElement;
  private sizeInput!: HTMLInputElement;
  private leadingInput!: HTMLInputElement;
  private trackingInput!: HTMLInputElement;
  private baselineShiftInput!: HTMLInputElement;
  private swatch!: HTMLDivElement;
  private boldBtn!: HTMLButtonElement;
  private italicBtn!: HTMLButtonElement;
  private underlineBtn!: HTMLButtonElement;
  private strikeBtn!: HTMLButtonElement;
  private fauxBoldBtn!: HTMLButtonElement;
  private fauxItalicBtn!: HTMLButtonElement;
  private allCapsBtn!: HTMLButtonElement;
  private smallCapsBtn!: HTMLButtonElement;
  private superscriptBtn!: HTMLButtonElement;
  private subscriptBtn!: HTMLButtonElement;
  private alignBtns!: HTMLButtonElement[];
  private antiAliasSelect!: HTMLSelectElement;

  // State
  private props: TypeProperties = { ...DEFAULTS };

  // Callback
  private onChange: ((props: TypeProperties) => void) | undefined;

  // Reused color picker (lazy)
  private colorPicker: ColorPicker | null = null;

  constructor(onChange?: (props: TypeProperties) => void) {
    this.onChange = onChange;
  }

  // ---------- Public API ----------

  getProps(): TypeProperties {
    return { ...this.props };
  }

  setProps(partial: Partial<TypeProperties>): void {
    Object.assign(this.props, partial);
    if (this.built) this.refreshAll();
  }

  setFonts(fonts: string[]): void {
    if (!this.built) return;
    this.familySelect.innerHTML = "";
    for (const f of fonts) {
      const o = document.createElement("option");
      o.value = f;
      o.textContent = f;
      this.familySelect.appendChild(o);
    }
    this.ensureFontOptionExists(this.props.fontFamily);
    this.familySelect.value = this.props.fontFamily;
  }

  /**
   * Guarantee the family <select> has an <option> matching `value` before we
   * assign `.value = value`. Without this, a font that isn't in the current
   * option list (e.g. a document created with a font not installed locally,
   * or before loadSystemFonts() has resolved) causes the native <select> to
   * silently fall back to blank/first-option — the field "goes blank" with
   * no indication of what font is actually set. Photoshop instead shows the
   * (missing) font's name as a placeholder entry, so we synthesize one.
   */
  private ensureFontOptionExists(value: string): void {
    if (!value || !this.familySelect) return;
    const exists = Array.from(this.familySelect.options).some((o) => o.value === value);
    if (!exists) {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = value;
      opt.dataset.missingFont = "true";
      this.familySelect.appendChild(opt);
    }
  }

  mount(container: HTMLElement): void {
    if (!this.built) this.build();
    container.appendChild(this.root);
  }

  destroy(): void {
    if (this.built && this.root.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
    if (this.colorPicker) {
      this.colorPicker.destroy();
      this.colorPicker = null;
    }
    this.built = false;
  }

  // ---------- Build (lazy) ----------

  private build(): void {
    this.root = el("div", "char-panel");

    // Title bar
    const title = el("div", "char-panel__title");
    title.textContent = "Character";
    this.root.appendChild(title);

    const body = el("div", "char-panel__body");

    // Row 1: Font family + Font style
    const fontRow = el("div", "char-panel__row");
    this.familySelect = this.makeSelectRow(fontRow, "Font", getAvailableFonts());
    this.styleSelect = this.makeSelectRow(fontRow, "Style", FONT_STYLES);
    body.appendChild(fontRow);

    // Row 2: Font size + Leading
    const sizeRow = el("div", "char-panel__row");
    this.sizeInput = this.makeNumberRow(sizeRow, "Size");
    this.leadingInput = this.makeNumberRow(sizeRow, "Leading");
    body.appendChild(sizeRow);

    // Row 3: Tracking + Baseline Shift
    const trackRow = el("div", "char-panel__row");
    this.trackingInput = this.makeNumberRow(trackRow, "Tracking");
    this.baselineShiftInput = this.makeNumberRow(trackRow, "Baseline");
    body.appendChild(trackRow);

    // Row 4: Color swatch + Anti-alias
    const colorRow = el("div", "char-panel__row");
    const colorGroup = el("div", "char-panel__group");
    const colorLabel = el("label", "char-panel__label");
    colorLabel.textContent = "Color";
    this.swatch = el("div", "char-panel__swatch");
    colorGroup.appendChild(colorLabel);
    colorGroup.appendChild(this.swatch);
    colorRow.appendChild(colorGroup);

    const aaGroup = el("div", "char-panel__group");
    const aaLabel = el("label", "char-panel__label");
    aaLabel.textContent = "AA";
    this.antiAliasSelect = el("select", "char-panel__select");
    for (const mode of ANTI_ALIAS_MODES) {
      const opt = document.createElement("option");
      opt.value = mode;
      opt.textContent = mode.charAt(0).toUpperCase() + mode.slice(1);
      this.antiAliasSelect.appendChild(opt);
    }
    aaGroup.appendChild(aaLabel);
    aaGroup.appendChild(this.antiAliasSelect);
    colorRow.appendChild(aaGroup);
    body.appendChild(colorRow);

    // Row 5: Bold / Italic / Underline / Strikethrough
    const btnRow = el("div", "char-panel__row");
    this.boldBtn = this.makeToggleBtn(btnRow, "B", "bold");
    this.italicBtn = this.makeToggleBtn(btnRow, "I", "italic");
    this.underlineBtn = this.makeToggleBtn(btnRow, "U", "underline");
    this.strikeBtn = this.makeToggleBtn(btnRow, "S", "strikethrough");
    body.appendChild(btnRow);

    // Row 6: Faux Bold / Faux Italic / All Caps / Small Caps
    const fauxRow = el("div", "char-panel__row");
    this.fauxBoldBtn = this.makeToggleBtn(fauxRow, "FB", "fauxBold");
    this.fauxItalicBtn = this.makeToggleBtn(fauxRow, "FI", "fauxItalic");
    this.allCapsBtn = this.makeToggleBtn(fauxRow, "TT", "allCaps");
    this.smallCapsBtn = this.makeToggleBtn(fauxRow, "tt", "smallCaps");
    body.appendChild(fauxRow);

    // Row 7: Superscript / Subscript
    const superRow = el("div", "char-panel__row");
    this.superscriptBtn = this.makeToggleBtn(superRow, "X²", "superscript");
    this.subscriptBtn = this.makeToggleBtn(superRow, "X₂", "subscript");
    body.appendChild(superRow);

    // Row 8: Align L / C / R / J (radio group)
    const alignRow = el("div", "char-panel__row");
    this.alignBtns = [];
    const alignments: Array<TypeProperties["align"]> = ["left", "center", "right", "justify"];
    const labels: Record<TypeProperties["align"], string> = { left: "L", center: "C", right: "R", justify: "J" };
    for (const a of alignments) {
      const btn = el("button", "char-panel__btn");
      btn.type = "button";
      btn.textContent = labels[a];
      btn.dataset.align = a;
      btn.addEventListener("click", () => this.setAlign(a));
      alignRow.appendChild(btn);
      this.alignBtns.push(btn);
    }
    body.appendChild(alignRow);

    this.root.appendChild(body);
    this.wireEvents();
    this.refreshAll();
    this.built = true;
  }

  private makeSelectRow(row: HTMLElement, label: string, options: string[]): HTMLSelectElement {
    const group = el("div", "char-panel__group");
    const lab = el("label", "char-panel__label");
    lab.textContent = label;
    const select = el("select", "char-panel__select");
    for (const opt of options) {
      const o = document.createElement("option");
      o.value = opt;
      o.textContent = opt;
      select.appendChild(o);
    }
    group.appendChild(lab);
    group.appendChild(select);
    row.appendChild(group);
    return select;
  }

  private makeNumberRow(row: HTMLElement, label: string): HTMLInputElement {
    const group = el("div", "char-panel__group");
    const lab = el("label", "char-panel__label");
    lab.textContent = label;
    const input = el("input", "char-panel__input");
    input.type = "number";
    input.spellcheck = false;
    group.appendChild(lab);
    group.appendChild(input);
    row.appendChild(group);
    return input;
  }

  private makeToggleBtn(
    row: HTMLElement,
    text: string,
    prop: "bold" | "italic" | "underline" | "strikethrough" | "fauxBold" | "fauxItalic" | "allCaps" | "smallCaps" | "superscript" | "subscript",
  ): HTMLButtonElement {
    const btn = el("button", "char-panel__btn");
    btn.type = "button";
    btn.textContent = text;
    btn.dataset.prop = prop;
    btn.addEventListener("click", () => {
      this.props[prop] = !this.props[prop];
      // Superscript and subscript are mutually exclusive in Photoshop:
      // turning one on forces the other off (i191).
      if (prop === "superscript" && this.props.superscript) {
        this.props.subscript = false;
      } else if (prop === "subscript" && this.props.subscript) {
        this.props.superscript = false;
      }
      // Bold/Italic toggle buttons and the Style dropdown both represent
      // the same underlying state — keep fontStyle in sync so there's a
      // single source of truth (i192).
      if (prop === "bold" || prop === "italic") {
        this.syncFontStyleFromBoldItalic();
      }
      this.refreshFormatBtns();
      this.emitChange();
    });
    row.appendChild(btn);
    return btn;
  }

  /** Derive props.fontStyle (and the Style dropdown) from bold/italic state. */
  private syncFontStyleFromBoldItalic(): void {
    const style = this.props.bold && this.props.italic
      ? "Bold Italic"
      : this.props.bold
        ? "Bold"
        : this.props.italic
          ? "Italic"
          : "Regular";
    this.props.fontStyle = style;
    if (this.built) this.styleSelect.value = style;
  }

  /** Derive props.bold/props.italic (and their toggle buttons) from the Style dropdown. */
  private applyFontStyleToBoldItalic(style: string): void {
    this.props.fontStyle = style;
    this.props.bold = style === "Bold" || style === "Bold Italic";
    this.props.italic = style === "Italic" || style === "Bold Italic";
    this.refreshFormatBtns();
  }

  private setAlign(align: TypeProperties["align"]): void {
    this.props.align = align;
    this.refreshAlign();
    this.emitChange();
  }

  // ---------- Events ----------

  private wireEvents(): void {
    this.familySelect.addEventListener("change", () => {
      this.props.fontFamily = this.familySelect.value;
      this.emitChange();
    });

    this.styleSelect.addEventListener("change", () => {
      this.applyFontStyleToBoldItalic(this.styleSelect.value);
      this.emitChange();
    });

    this.sizeInput.addEventListener("change", () => {
      const v = parseFloat(this.sizeInput.value);
      if (Number.isFinite(v) && v > 0) {
        this.props.fontSize = v;
        this.emitChange();
      }
    });

    this.leadingInput.addEventListener("change", () => {
      const v = parseFloat(this.leadingInput.value);
      if (Number.isFinite(v) && v > 0) {
        this.props.leading = v;
        this.emitChange();
      }
    });

    this.trackingInput.addEventListener("change", () => {
      const v = parseFloat(this.trackingInput.value);
      if (Number.isFinite(v)) {
        this.props.tracking = v;
        this.emitChange();
      }
    });

    this.baselineShiftInput.addEventListener("change", () => {
      const v = parseFloat(this.baselineShiftInput.value);
      if (Number.isFinite(v)) {
        this.props.baselineShift = v;
        this.emitChange();
      }
    });

    this.antiAliasSelect.addEventListener("change", () => {
      this.props.antiAlias = this.antiAliasSelect.value as AntiAliasMode;
      this.emitChange();
    });

    this.swatch.addEventListener("click", () => this.openColorPicker());
  }

  private async openColorPicker(): Promise<void> {
    if (!this.colorPicker) this.colorPicker = new ColorPicker();
    const result = await this.colorPicker.open(this.props.color);
    if (result) {
      this.props.color = { r: result.r, g: result.g, b: result.b };
      this.refreshSwatch();
      this.emitChange();
    }
  }

  // ---------- Refresh ----------

  private refreshAll(): void {
    this.ensureFontOptionExists(this.props.fontFamily);
    this.familySelect.value = this.props.fontFamily;
    this.styleSelect.value = this.props.fontStyle;
    this.sizeInput.value = String(this.props.fontSize);
    this.leadingInput.value = String(this.props.leading);
    this.trackingInput.value = String(this.props.tracking);
    this.baselineShiftInput.value = String(this.props.baselineShift);
    this.refreshSwatch();
    this.refreshFormatBtns();
    this.refreshAlign();
    this.antiAliasSelect.value = this.props.antiAlias;
  }

  private refreshSwatch(): void {
    const { r, g, b } = this.props.color;
    this.swatch.style.background = `rgb(${r},${g},${b})`;
  }

  private refreshFormatBtns(): void {
    this.boldBtn.classList.toggle("char-panel__btn--active", this.props.bold);
    this.italicBtn.classList.toggle("char-panel__btn--active", this.props.italic);
    this.underlineBtn.classList.toggle("char-panel__btn--active", this.props.underline);
    this.strikeBtn.classList.toggle("char-panel__btn--active", this.props.strikethrough);
    this.fauxBoldBtn.classList.toggle("char-panel__btn--active", this.props.fauxBold);
    this.fauxItalicBtn.classList.toggle("char-panel__btn--active", this.props.fauxItalic);
    this.allCapsBtn.classList.toggle("char-panel__btn--active", this.props.allCaps);
    this.smallCapsBtn.classList.toggle("char-panel__btn--active", this.props.smallCaps);
    this.superscriptBtn.classList.toggle("char-panel__btn--active", this.props.superscript);
    this.subscriptBtn.classList.toggle("char-panel__btn--active", this.props.subscript);
  }

  private refreshAlign(): void {
    for (const btn of this.alignBtns) {
      const active = btn.dataset.align === this.props.align;
      btn.classList.toggle("char-panel__btn--active", active);
    }
  }

  private emitChange(): void {
    this.onChange?.({ ...this.props });
  }
}
